import { useState, useCallback } from "react";
import { calendarService } from "../services/calendarService";

export const useCalendar = ({ workspaceId } = {}) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- External Integrations ---

  const connectExternalCalendar = async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await calendarService.connectExternal(payload);
      return response;
    } catch (err) {
      setError(err.message || "Failed to connect calendar");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const syncExternalCalendar = async (configId) => {
    setLoading(true);
    try {
      const response = await calendarService.syncExternal(configId);
      return response;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const [connectedCalendars, setConnectedCalendars] = useState([]);

  const loadConnectedCalendars = useCallback(async () => {
    try {
      const data = await calendarService.getConnectedCalendars();
      setConnectedCalendars(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load connected calendars", err);
    }
  }, []);

  const disconnectCalendar = async (configId) => {
    setLoading(true);
    try {
      await calendarService.disconnectCalendar(configId);
      setConnectedCalendars(prev => prev.filter(c => c.id !== configId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // --- Internal Calendar Operations ---

  const loadEvents = useCallback(async (start, end) => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await calendarService.getEvents(workspaceId, start, end);
      setEvents(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      console.error("Failed to load events", err);
      setError("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  const addEvent = async (eventData) => {
    try {
      const newEvent = await calendarService.createEvent({ ...eventData, workspaceId });
      setEvents(prev => [...prev, newEvent]);
      return newEvent;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const editEvent = async (eventId, eventData) => {
    try {
      const updated = await calendarService.updateEvent(eventId, eventData);
      setEvents(prev => prev.map(e => e.id === eventId ? updated : e));
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const removeEvent = async (eventId) => {
    try {
      await calendarService.deleteEvent(eventId);
      setEvents(prev => prev.filter(e => e.id !== eventId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const modifyInstance = async (data) => {
    try {
      const exception = await calendarService.modifyInstance({ ...data, workspaceId });
      return exception;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const cancelInstance = async (data) => {
    try {
      await calendarService.cancelInstance({ ...data, workspaceId });
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    events,
    loading,
    error,
    loadEvents,
    addEvent,
    editEvent,
    removeEvent,
    modifyInstance,
    cancelInstance,
    connectExternalCalendar,
    syncExternalCalendar,
    connectedCalendars,
    loadConnectedCalendars,
    disconnectCalendar
  };
};